import React from 'react';

import { Box, Icon, ListRow, Text } from '@betterment/samba-react';

import { toTitleCase } from '../../../../../utils/StringHelpers';
import { formattedActivityDate } from '../../../utils/dateUtils';
import { ActivityItemStage, type ActivityItemStatus } from '../transactionTypes/data';

interface StatusSectionProps {
  statuses: ActivityItemStatus[];
}

const StatusIcon: React.FC<{ status: ActivityItemStatus }> = ({ status }) => {
  if (status.stage === ActivityItemStage.Canceled || status.stage === ActivityItemStage.Failed) {
    return <Icon name="XCircle" color="icon.critical" size="small" data-testid="status-redx" />;
  }
  if (status.stage === ActivityItemStage.Returned) {
    return <Icon name="Undo" color="icon" size="small" data-testid="status-returned" />;
  }

  const iconName = status.timestamp ? 'CheckmarkCircleFilled' : 'CheckmarkCircle';
  const color = status.timestamp ? 'icon.success' : 'icon.weakest';
  return <Icon name={iconName} color={color} size="small" data-testid={iconName} />;
};

export const StatusSection: React.FC<StatusSectionProps> = ({ statuses }) => {
  return (
    <Box css={{ pt: 'space.100', pb: 'space.100' }}>
      {statuses.map(status => {
        const formattedDate = formattedActivityDate(status.timestamp);
        return (
          <ListRow key={status.stage} css={{ px: 0, pb: 'space.200', pt: 'space.200' }}>
            <ListRow.LeadingAsset>
              <StatusIcon status={status} />
            </ListRow.LeadingAsset>
            <ListRow.LeadingContent>
              <Text variant="text100">{toTitleCase(status.stage)}</Text>
              {formattedDate && status.stage !== ActivityItemStage.Canceled && (
                <Text variant="text50" color="text.weak">
                  {formattedDate}
                </Text>
              )}
            </ListRow.LeadingContent>
            {(status.description || status.message) && (
              <ListRow.BottomContent
                alignment="inset"
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'space.100',
                }}
              >
                {status.description && (
                  <Text variant="text50" color="text.weak">
                    {status.description}
                  </Text>
                )}
                {status.message && (
                  <Text variant="text50" color="text.weak">
                    {status.message}
                  </Text>
                )}
              </ListRow.BottomContent>
            )}
          </ListRow>
        );
      })}
    </Box>
  );
};
